/**
 * Wetter am Event-Ort: jetzt, die naechsten Stunden, die naechsten Tage.
 *
 * Die Daten kommen von MET Norway (yr.no) -- weltweit, kostenlos und unter
 * CC BY 4.0 auch kommerziell nutzbar, solange die Quelle dabeisteht. Geholt
 * werden sie vom Server, nicht von den Zuschauern: die Anfrage traegt nur
 * die Koordinaten des Events, und alle sehen dieselbe Vorhersage.
 */

/** Was die Einblendung zeigt. */
export type WeatherView = 'now' | 'hours' | 'days';

export const WEATHER_VIEWS: { id: WeatherView; label: string; hint: string }[] = [
  { id: 'now', label: 'Jetzt', hint: 'Temperatur, Himmel, Wind und Regenrisiko gerade.' },
  { id: 'hours', label: 'Nächste Stunden', hint: 'Die kommenden Stunden nebeneinander — beim Backyard ist jede ein Loop.' },
  { id: 'days', label: 'Heute/Morgen', hint: 'Höchst- und Tiefstwert, Regenmenge.' },
];

/** Einstellung der Regie. `place` ueberschreibt den Ort des Events. */
export interface WeatherConfig {
  view: WeatherView;
  place: string | null;
}

export const defaultWeatherConfig = (): WeatherConfig => ({ view: 'hours', place: null });

export interface WeatherHour {
  /** Beginn der Stunde, Unix-ms. */
  at: number;
  /** `14 Uhr` in der Zeitzone des Events -- gerechnet am Server, damit alle dasselbe lesen. */
  label: string;
  temp: number;
  /** Symbolschluessel von MET Norway, z. B. `partlycloudy_day`. */
  symbol: string;
  precipMm: number;
  /** Regenwahrscheinlichkeit in Prozent, wenn MET Norway eine liefert. */
  precipChance: number | null;
  windKmh: number;
}

export interface WeatherDay {
  /** `Heute`, `Morgen`, dann der Wochentag. */
  label: string;
  tempMax: number;
  tempMin: number;
  precipMm: number;
  symbol: string;
}

export interface WeatherReport {
  /** Ortsname fuer die Anzeige. */
  place: string;
  timeZone: string;
  /** Wann der Server die Vorhersage geholt hat. */
  fetchedAt: number;
  now: WeatherHour;
  hours: WeatherHour[];
  days: WeatherDay[];
}

/** Was im Raum herumgeht: Einstellung, letzte Vorhersage, und warum es keine gibt. */
export interface WeatherState {
  config: WeatherConfig;
  report: WeatherReport | null;
  error: string | null;
}

export const emptyWeather = (): WeatherState => ({ config: defaultWeatherConfig(), report: null, error: null });

/** Die Quellenangabe, die CC BY 4.0 verlangt. */
export const WEATHER_CREDIT = 'Wetterdaten: MET Norway';

/**
 * Symbol und Wort zu einem MET-Symbolschluessel. Die Schluessel tragen
 * Tageszeit (`_day`, `_night`, `_polartwilight`) und Staerke (`light`,
 * `heavy`) -- fuer ein Zeichen im Bild reicht die Grundart.
 */
export function weatherIcon(symbol: string): { icon: string; text: string } {
  const night = symbol.endsWith('_night');
  const base = symbol.replace(/_(day|night|polartwilight)$/, '');
  if (base.includes('thunder')) return { icon: '⛈️', text: 'Gewitter' };
  if (base.includes('sleet')) return { icon: '🌨️', text: 'Schneeregen' };
  if (base.includes('snow')) return { icon: '❄️', text: 'Schnee' };
  if (base.endsWith('showers')) return { icon: night ? '🌧️' : '🌦️', text: 'Schauer' };
  if (base.includes('rain')) return { icon: '🌧️', text: base.startsWith('heavy') ? 'starker Regen' : 'Regen' };
  if (base === 'fog') return { icon: '🌫️', text: 'Nebel' };
  if (base === 'cloudy') return { icon: '☁️', text: 'bewölkt' };
  if (base === 'partlycloudy') return { icon: night ? '☁️' : '⛅', text: 'teils bewölkt' };
  if (base === 'fair') return { icon: night ? '🌙' : '🌤️', text: 'heiter' };
  if (base === 'clearsky') return { icon: night ? '🌙' : '☀️', text: 'klar' };
  return { icon: '🌡️', text: '' };
}

/** `15°` -- ganze Grad reichen fuers Bild. */
export const formatTemp = (celsius: number): string => `${Math.round(celsius)}°`;
